// Generated by Copilot — Migrated from Userfront to Clerk
import React, { useState, useEffect, useCallback } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useClerk } from '@clerk/react';
import useAuthHeaders from '../hooks/useAuthHeaders';
import { LayoutDashboard, FileText, Search, HelpCircle, LogOut, Settings, CreditCard, Sparkles, Activity, Menu, X, ShieldCheck, BarChart2, FlaskConical, Mail, Receipt, Building2, ExternalLink, SlidersHorizontal, ScrollText, FileCheck2 } from 'lucide-react';
import useRole from '../hooks/useRole';
import TermsAcceptanceGate from './TermsAcceptanceGate';
import TermsHistoryModal from './TermsHistoryModal';
import './MainLayout.css';

const API_URL = process.env.REACT_APP_API_URL;
const DESPACHO_URL = process.env.REACT_APP_DESPACHO_URL;

const MainLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [allowedFeatures, setAllowedFeatures] = useState([]);
  const [apiVersion, setApiVersion] = useState(null);
  const [showTermsHistory, setShowTermsHistory] = useState(false);
  const { signOut } = useClerk();
  const { email, getToken } = useAuthHeaders();
  const { isSuperAdmin } = useRole();

  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        const token = await getToken();
        const response = await fetch(`${API_URL}/Users/features`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setAllowedFeatures(data.features || []);
        }
      } catch {
        // Silently fail — features just won't show
      }
    };
    fetchFeatures();
  }, [getToken]);

  useEffect(() => {
    fetch(`${API_URL}/version`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setApiVersion(data); })
      .catch(() => {});
  }, []);

  const handleLogout = () => {
    signOut();
  };

  const allMenuItems = [
    { title: 'Dashboard', path: '/', icon: LayoutDashboard },
    { title: 'Mis RFC', path: '/listado-rfc', icon: FileText },
    { title: 'API & Consultas', path: '/consultar', icon: Search },
    { title: 'API Tester', path: '/playground', icon: FlaskConical },
    { title: 'Facturin', path: '/facturin', icon: Sparkles, feature: 'facturin' },
    { title: 'Suscripciones', path: '/suscripciones', icon: CreditCard },
    { title: 'Mis Facturas', path: '/facturas', icon: Receipt },
    { title: 'Status', path: '/status', icon: Activity },
    { title: 'Bitácora', path: '/bitacora', icon: ScrollText },
    { title: 'Constancias de eliminación', path: '/constancias-eliminacion', icon: FileCheck2 },
    { title: 'Datos Fiscales', path: '/datos-fiscales', icon: Building2 },
    { title: 'Portal del Cliente', url: DESPACHO_URL, icon: ExternalLink, external: true, badge: 'Beta' },
    //  { title: 'Configuracion', path: '/configuracion', icon: Settings },
    { title: 'Ayuda', path: '/ayuda', icon: HelpCircle },
    { title: 'Admin', path: '/admin', icon: ShieldCheck, adminOnly: true },
    { title: 'Reportes', path: '/admin/reports', icon: BarChart2, adminOnly: true },
    { title: 'Enviar Correos', path: '/admin/send-emails', icon: Mail, adminOnly: true },
    { title: 'Features y Límites', path: '/admin/features', icon: SlidersHorizontal, adminOnly: true },
    { title: 'Términos y Condiciones', path: '/admin/terms', icon: ScrollText, adminOnly: true },
  ];

  const menuItems = allMenuItems.filter(item =>
    (!item.feature || allowedFeatures.includes(item.feature)) &&
    (!item.adminOnly || isSuperAdmin) &&
    (!item.external || item.url)
  );

  const isActive = (path) => location.pathname === path;

  // Get user initials for avatar
  const getUserInitials = () => {
    return email ? email.charAt(0).toUpperCase() : '';
  };

  return (
    <div className="main-layout">
      {/* Gate de aceptación de T&C: modal bloqueante si la cuenta no ha aceptado la versión vigente */}
      <TermsAcceptanceGate />

      {/* Historial de aceptaciones (cláusula 4.4), accesible desde el submenú del usuario */}
      <TermsHistoryModal show={showTermsHistory} onHide={() => setShowTermsHistory(false)} />

      {/* Header */}
      <header className="main-header">
        <div className="header-container">
          <div className="brand-section">
            <button
              className="hamburger-btn"
              onClick={() => setSidebarOpen(prev => !prev)}
              aria-label="Abrir menú"
            >
              {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
            <img src="/satgo-banner-bg.png" alt="SatGO" className="brand-logo" />
          </div>

          <div className="user-section">
            <Dropdown align="end">
              <Dropdown.Toggle as="div" className="user-info user-menu-toggle" role="button">
                <div className="user-avatar">{getUserInitials()}</div>
                <span className="user-email">{email}</span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => navigate('/datos-fiscales')}>
                  <Receipt size={16} className="me-2" />
                  Datos Fiscales
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setShowTermsHistory(true)}>
                  <ScrollText size={16} className="me-2" />
                  Términos y Condiciones
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={handleLogout}>
                  <LogOut size={16} className="me-2" />
                  Cerrar sesión
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </header>

      {/* Main Content Wrapper */}
      <div className="main-content-wrapper">
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Sidebar */}
        <aside className={`main-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-content">
            <nav className="sidebar-menu">
              {menuItems.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div
                    key={index}
                    className={`menu-item ${isActive(item.path) ? 'active' : ''}`}
                    onClick={() => {
                      if (item.external) {
                        window.open(item.url, '_blank', 'noopener,noreferrer');
                      } else {
                        navigate(item.path);
                      }
                      setSidebarOpen(false);
                    }}
                  >
                    <IconComponent className="menu-icon" size={20} />
                    <span>{item.title}</span>
                    {item.badge && <span className="menu-badge">{item.badge}</span>}
                  </div>
                );
              })}
            </nav>

            {/* Logout Section - Before Footer */}
            <div className="logout-section">
              <div
                className="menu-item logout-item"
                onClick={handleLogout}
              >
                <LogOut className="menu-icon" size={20} />
                <span>Cerrar sesión</span>
              </div>
            </div>

            {/* Footer in Sidebar */}
            <div className="sidebar-footer">
              <div className="footer-copyright">
                © {new Date().getFullYear()} SatGo
              </div>
              <div className="footer-made-with">
                Hecho con <span className="footer-heart">❤️</span> en México
              </div>
              {apiVersion && (
                <div
                  className="footer-version"
                  title={apiVersion.commit ? `commit ${apiVersion.commit}` : ''}
                >
                  API v{apiVersion.version}
                  {apiVersion.commit && ` · ${apiVersion.commit.substring(0, 7)}`}
                </div>
              )}
            </div>
          </div>
        </aside>

        {/* Content Area */}
        <main className="content-area">
          {children}
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
